// Les faits d'une offre, dits en mots (issue #169). Module PUR et sans
// dépendance : l'écran (cartes de l'accueil, en-tête de la fiche) et le mail
// d'alerte disent les mêmes faits avec les mêmes mots.
//
// Les faits viennent de `offer_criteres`, que la nuit lit par l'IA pour les
// critères qui excluent (`services/criteresOffre.ts`). Ils servaient à cacher ;
// ils servent maintenant aussi à MONTRER : contrat, télétravail, salaire,
// langues exigées, ce qu'un candidat veut savoir avant le score.
//
// UNE OFFRE MUETTE NE DIT RIEN. Un champ nul ne produit aucun mot : « Contrat
// non précisé » sur chaque carte serait du bruit, et « sur site » par défaut
// serait faux. Un salaire hors de `SALAIRE_PLAUSIBLE` (un tarif journalier lu
// comme annuel) est tu, comme dans le bilan du marché.

export interface FaitsOffre {
  contrat: string | null
  teletravail: string | null
  langues: string[]
  niveau: string | null
  salaireMin: number | null
  salaireMax: number | null
  /** Temps de travail (issue #175) : `plein` ne se dit pas, c'est l'ordinaire. */
  temps?: string | null
}

import type { UiLang } from '@ai-job-fit/engine/langues'

export type LangueFaits = UiLang

/** Même borne que le bilan du marché (`services/marche.ts`). */
export const SALAIRE_PLAUSIBLE = { min: 15_000, max: 300_000 }

const MOTS = {
  fr: {
    contrat: { cdi: 'CDI', cdd: 'CDD', interim: 'Intérim', freelance: 'Freelance', stage: 'Stage' } as Record<string, string>,
    teletravail: { sur_site: 'Sur site', hybride: 'Hybride', complet: 'Télétravail complet' } as Record<string, string>,
    langue: { fr: 'français', nl: 'néerlandais', en: 'anglais', de: 'allemand' } as Record<string, string>,
    niveau: { junior: 'Junior', confirme: 'Confirmé', senior: 'Senior' } as Record<string, string>,
    temps: { partiel: 'Temps partiel', les_deux: 'Temps partiel possible' } as Record<string, string>,
    fourchette: (a: string, b: string) => `${a} à ${b}`,
    aPartir: (a: string) => `À partir de ${a}`,
    jusqua: (b: string) => `Jusqu’à ${b}`,
    brutAn: (s: string) => `${s}\u00a0€ brut par an`,
  },
  en: {
    contrat: { cdi: 'Permanent', cdd: 'Fixed-term', interim: 'Temp agency', freelance: 'Freelance', stage: 'Internship' } as Record<string, string>,
    teletravail: { sur_site: 'On site', hybride: 'Hybrid', complet: 'Fully remote' } as Record<string, string>,
    langue: { fr: 'French', nl: 'Dutch', en: 'English', de: 'German' } as Record<string, string>,
    niveau: { junior: 'Junior', confirme: 'Mid-level', senior: 'Senior' } as Record<string, string>,
    temps: { partiel: 'Part-time', les_deux: 'Part-time possible' } as Record<string, string>,
    fourchette: (a: string, b: string) => `${a} to ${b}`,
    aPartir: (a: string) => `From ${a}`,
    jusqua: (b: string) => `Up to ${b}`,
    brutAn: (s: string) => `${s} gross a year`,
  },
  nl: {
    contrat: { cdi: 'Vast contract', cdd: 'Tijdelijk contract', interim: 'Interim', freelance: 'Freelance', stage: 'Stage' } as Record<string, string>,
    teletravail: { sur_site: 'Op locatie', hybride: 'Hybride', complet: 'Volledig telewerk' } as Record<string, string>,
    langue: { fr: 'Frans', nl: 'Nederlands', en: 'Engels', de: 'Duits' } as Record<string, string>,
    niveau: { junior: 'Junior', confirme: 'Ervaren', senior: 'Senior' } as Record<string, string>,
    temps: { partiel: 'Deeltijds', les_deux: 'Deeltijds mogelijk' } as Record<string, string>,
    fourchette: (a: string, b: string) => `${a} tot ${b}`,
    aPartir: (a: string) => `Vanaf ${a}`,
    jusqua: (b: string) => `Tot ${b}`,
    brutAn: (s: string) => `${s} bruto per jaar`,
  },
  it: {
    contrat: { cdi: 'Tempo indeterminato', cdd: 'Tempo determinato', interim: 'Interinale', freelance: 'Freelance', stage: 'Tirocinio' } as Record<string, string>,
    teletravail: { sur_site: 'In sede', hybride: 'Ibrido', complet: 'Telelavoro completo' } as Record<string, string>,
    langue: { fr: 'francese', nl: 'olandese', en: 'inglese', de: 'tedesco' } as Record<string, string>,
    niveau: { junior: 'Junior', confirme: 'Esperto', senior: 'Senior' } as Record<string, string>,
    temps: { partiel: 'Part-time', les_deux: 'Part-time possibile' } as Record<string, string>,
    fourchette: (a: string, b: string) => `da ${a} a ${b}`,
    aPartir: (a: string) => `Da ${a}`,
    jusqua: (b: string) => `Fino a ${b}`,
    brutAn: (s: string) => `${s} lordi all’anno`,
  },
  es: {
    contrat: { cdi: 'Indefinido', cdd: 'Temporal', interim: 'ETT', freelance: 'Autónomo', stage: 'Prácticas' } as Record<string, string>,
    teletravail: { sur_site: 'Presencial', hybride: 'Híbrido', complet: 'Teletrabajo total' } as Record<string, string>,
    langue: { fr: 'francés', nl: 'neerlandés', en: 'inglés', de: 'alemán' } as Record<string, string>,
    niveau: { junior: 'Junior', confirme: 'Con experiencia', senior: 'Senior' } as Record<string, string>,
    temps: { partiel: 'Media jornada', les_deux: 'Media jornada posible' } as Record<string, string>,
    fourchette: (a: string, b: string) => `de ${a} a ${b}`,
    aPartir: (a: string) => `Desde ${a}`,
    jusqua: (b: string) => `Hasta ${b}`,
    brutAn: (s: string) => `${s} brutos al año`,
  },
} satisfies Record<LangueFaits, unknown>

const plausible = (n: number | null): n is number =>
  typeof n === 'number' && n >= SALAIRE_PLAUSIBLE.min && n <= SALAIRE_PLAUSIBLE.max

function montant(n: number, lang: LangueFaits): string {
  // Espace insécable pleine en français (« 53 600 ») : l'espace fine que rend
  // `toLocaleString` se voyait à peine, et la pastille lisait « 65000 ».
  const r = Math.round(n)
  if (lang === 'fr') return r.toLocaleString('fr-FR').replace(/[\s\u00a0\u202f]/g, '\u00a0')
  if (lang === 'nl') return `€\u00a0${r.toLocaleString('nl-BE')}`
  if (lang === 'it') return `${r.toLocaleString('it-IT')}\u00a0€`
  // `es-ES` ne groupe pas les nombres de quatre chiffres (« 9500 ») : on force le groupement.
  if (lang === 'es') return `${r.toLocaleString('es-ES', { useGrouping: 'always' } as Intl.NumberFormatOptions)}\u00a0€`
  return `€${r.toLocaleString('en-GB')}`
}

/**
 * Le salaire affiché, en une puce, ou null. La nuit range tout salaire en brut
 * ANNUEL (`criteresPrompt` convertit un mensuel), la puce le dit donc : un
 * « 65 000 à 80 000 € » sans brut ni période se lisait comme un net mensuel
 * ou un coût employeur. L'euro n'apparaît qu'une fois en français.
 */
export function salaireEnMots(f: Pick<FaitsOffre, 'salaireMin' | 'salaireMax'>, lang: LangueFaits): string | null {
  const m = MOTS[lang]
  const a = plausible(f.salaireMin) ? f.salaireMin : null
  const b = plausible(f.salaireMax) ? f.salaireMax : null
  const e = (n: number) => montant(n, lang)
  let s: string | null = null
  if (a !== null && b !== null) s = a === b ? e(a) : m.fourchette(e(Math.min(a, b)), e(Math.max(a, b)))
  else if (a !== null) s = m.aPartir(e(a))
  else if (b !== null) s = m.jusqua(e(b))
  return s === null ? null : m.brutAn(s)
}

/**
 * Les faits dits par l'offre, dans l'ordre où un candidat les cherche :
 * contrat, télétravail, salaire, langues exigées, niveau. Vide si l'offre ne
 * dit rien, ou si la nuit ne l'a pas encore lue.
 */
export function faitsEnMots(f: FaitsOffre | null | undefined, lang: LangueFaits): string[] {
  if (!f) return []
  const m = MOTS[lang]
  const langues = f.langues.map((l) => m.langue[l]).filter(Boolean)
  const premiereMajuscule = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
  return [
    f.contrat ? m.contrat[f.contrat] : null,
    f.temps ? m.temps[f.temps] ?? null : null,
    f.teletravail ? m.teletravail[f.teletravail] : null,
    salaireEnMots(f, lang),
    langues.length ? premiereMajuscule(langues.join(', ')) : null,
    f.niveau ? m.niveau[f.niveau] : null,
  ].filter((x): x is string => !!x)
}

/** Les colonnes de `offer_criteres` lues sous un alias (`k_contrat`…), telles
 *  que les requêtes de l'accueil et du mail les sélectionnent. */
export function faitsDeLigne(r: Record<string, unknown>): FaitsOffre | null {
  if (!r.k_lu) return null
  const n = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v))
  return {
    contrat: (r.k_contrat as string) ?? null,
    teletravail: (r.k_teletravail as string) ?? null,
    langues: r.k_langues ? String(r.k_langues).split(',').filter(Boolean) : [],
    niveau: (r.k_niveau as string) ?? null,
    salaireMin: n(r.k_salaire_min),
    salaireMax: n(r.k_salaire_max),
    temps: (r.k_temps as string) ?? null,
  }
}

/** Le fragment SQL qui va avec `faitsDeLigne` : à joindre sous l'alias `k`. */
export const COLONNES_FAITS =
  'k.offer_id AS k_lu, k.contrat AS k_contrat, k.teletravail AS k_teletravail, k.langues AS k_langues, k.niveau AS k_niveau, k.salaire_min AS k_salaire_min, k.salaire_max AS k_salaire_max, k.temps AS k_temps'
